<template>
  <div id="DistributorSearchMap">
    <div
      id="AzureAtlas"
    />
    <div
      id="AzureAtlasPlaceHolder"
      v-show="!showMap"
    />
  </div>
</template>
<script>
import {hasConstructor} from '~/assets/js/utils/object_helper'

export default {
  props: {
    distributors: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    region: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    points: {
      type: Array,
      default() {
        return []
      }
    },
    distributorSelected: {
      type: Boolean,
      default() {
        return false
      }
    },
    localAtlas: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    localMap: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    localMapClient: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    refreshId: {
      type: Number,
      required: false,
      default() {
        return null
      }
    },
    focussedDistributor: {
      type: Object,
      required: false,
      default() {
        return null
      }
    }
  },
  data() {
    return {
      loadedAtleastOnce: false,
      showMap: false,
      mapCenter: {lat: 51.164, lng: 10.454},
      infoWindow: null,
      clickedMarker: [],
      timeoutId: null,
      layerIds: [],
      markerLoaderCounter: 0,
      markerLoaderMax: 20,
      pinsLoadCheckCounter: 0,
      pinsLoadCheckMax: 20,
      pinsLoadCheckTimeoutId: null,
      mapInitAttempts: 0,
      mapInitMax: 20,
      htmlMarkerLoaderCounter: 0,
      htmlMarkerLoaderMax: 20,
      currentDataSource: null,
      mapReadyHandled: false,
      toastShown: false
    }
  },
  beforeUnmount() {
    if (this.pinsLoadCheckTimeoutId) {
      clearTimeout(this.pinsLoadCheckTimeoutId)
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  },
  methods: {
    initMap() {
      const depsReady = this.localMap && this.localAtlas
      if (!depsReady) {
        this.mapInitAttempts += 1
        if (this.mapInitAttempts > this.mapInitMax) {
          this.showMapLoadErrorOnce('Map initialization timeout after ' + this.mapInitMax + ' attempts')
          return
        }
        setTimeout(() => {
          this.initMap()
        }, 250)
        return
      }

      this.mapInitAttempts = 0

      const onReady = async () => {
        if (this.mapReadyHandled) {
          return
        }
        try {
          await this.waitForMapReady()
        } catch {
          this.showMapLoadErrorOnce('Map ready timeout')
          return
        }

        this.mapReadyHandled = true

        this.initMarkerClusterer()

        if (
          this.distributorSelected &&
          Array.isArray(this.distributors) &&
          this.distributors.length &&
          this.distributors[0].gps
        ) {
          const {lng, lat} = this.distributors[0].gps
          this.focusOnPoints([{lng, lat}])
        }

        this.loadedAtleastOnce = true
        setTimeout(() => {
          this.showMap = true
        }, 1000)
      }

      if (this.localMap?.events) {
        this.localMap.events.addOnce('ready', onReady)
      }

      setTimeout(() => {
        onReady()
      }, 250)

      if (this.localMap?.events) {
        this.localMap.events.add('zoomend', () => {
          this.clearClickedMarkers()
        })
      }
    },
    clearClickedMarkers() {
      let that = this
      return new Promise((resolve) => {
        let closedMarkers = []
        if (that.clickedMarker.length > 0) {
          that.clickedMarker.forEach(marker => {
            if (marker?.options?.popup?.isOpen()) {
              const distributorId = marker.properties?.id
              if (distributorId && distributorId !== this.focussedDistributor?.Id) {
                marker?.togglePopup()
                closedMarkers.push(marker)
              }
            }
          })
        }
        that.clickedMarker = that.clickedMarker.filter(marker => !closedMarkers.includes(marker))
        resolve()
      })
    },
    resetZoom() {
      this.localMap?.setCamera({
        center: [this.mapCenter.lng, this.mapCenter.lat],
        zoom: 1
      })
      this.clearClickedMarkers()
    },
    clearMarkers() {
      this.localMap.markers.clear()
    },
    waitForMapReady(maxMs = 15000, intervalMs = 250) {
      const start = Date.now()
      return new Promise((resolve, reject) => {
        const checkReady = () => {
          const elapsed = Date.now() - start
          const atlasReady = !!(this.localAtlas && this.localAtlas.data && this.localAtlas.layer && this.localAtlas.source)
          const mapReady = !!(this.localMap && this.localMap.sources && typeof this.localMap.sources.add === 'function')
          const markerLayerReady = !!(this.localAtlas?.layer?.HtmlMarkerLayer)

          if (atlasReady && mapReady && markerLayerReady) {
            resolve(true)
            return
          }

          if (elapsed >= maxMs) {
            reject(new Error('Map not ready after waiting'))
            return
          }

          setTimeout(checkReady, intervalMs)
        }

        checkReady()
      })
    },
    sanitizeUrl(url) {
      if (/^http(s?):\/\//i.test(url)) {
        return url
      } else {
        return 'http://' + url
      }
    },
    async initMarkerClusterer() {
      try {
        await this.waitForMapReady()
      } catch {
        this.showMapLoadErrorOnce('Marker clusterer: map not ready')
        return
      }
      // Clear any existing pin load check
      if (this.pinsLoadCheckTimeoutId) {
        clearTimeout(this.pinsLoadCheckTimeoutId)
        this.pinsLoadCheckTimeoutId = null
      }

      // Retry up to ~10s if localAtlas.data is not available
      if (!this.localAtlas?.data) {
        this.markerLoaderCounter++
        if (this.markerLoaderCounter < this.markerLoaderMax) {
          setTimeout(() => {
            this.initMarkerClusterer()
          }, 250)
          return
        } else {
          this.showMapLoadErrorOnce('Atlas data not available after ' + this.markerLoaderMax + ' retries')
          this.markerLoaderCounter = 0
          return
        }
      }

      await this.clearClickedMarkers()
      const that = this
      const safeDistributors = Array.isArray(this.distributors)
        ? this.distributors.filter(d => d?.gps?.lng && d?.gps?.lat)
        : []

      if (!safeDistributors.length) {
        this.teardownLayers()
        this.pinsLoadCheckCounter = 0
        return
      }

      let distributorsData
      if (this.localAtlas?.data) {
        try {
          distributorsData = safeDistributors.map(d => {
            return new this.localAtlas.data.Feature(new this.localAtlas.data.Point([d.gps.lng, d.gps.lat]), {
              distributor: d,
              id: d.Id
            })
          })
        } catch {
          this.showMapLoadErrorOnce('Failed to map distributor data to atlas features')
          return
        }
      } else {
        this.showMapLoadErrorOnce('Atlas data object not available')
        return
      }

      const datasource = new this.localAtlas.source.DataSource(null, {
        cluster: true,
        clusterRadius: 30,
        clusterMaxZoom: 5
      })

      const sources = this.localMap?.sources
      if (!sources || typeof sources.add !== 'function') {
        return
      }

      this.teardownLayers()

      sources.add(datasource)
      datasource.add(distributorsData)
      this.currentDataSource = datasource

      if (!hasConstructor(this.localAtlas.layer.HtmlMarkerLayer)) {
        this.htmlMarkerLoaderCounter++
        if (this.htmlMarkerLoaderCounter <= this.htmlMarkerLoaderMax) {
          setTimeout(() => {
            this.initMarkerClusterer()
          }, 250)
          return
        }
        this.showMapLoadErrorOnce('HtmlMarkerLayer not available after ' + this.htmlMarkerLoaderMax + ' retries')
        this.htmlMarkerLoaderCounter = 0
        return
      }
      this.htmlMarkerLoaderCounter = 0

      // Ensure map and map.events are available before creating layer
      if (!this.localMap || !this.localMap.events) {
        this.showMapLoadErrorOnce('Map or map.events not available for HtmlMarkerLayer')
        return
      }

      const layer = new this.localAtlas.layer.HtmlMarkerLayer(datasource, null, {
        markerCallback: (id, position, properties) => {
          const rawDistributor = properties?.distributor
          let distributor = rawDistributor || {}
          if (typeof rawDistributor === 'string') {
            try {
              distributor = JSON.parse(rawDistributor)
            } catch {
              distributor = {}
            }
          }

          if (properties.cluster) {

            const color = properties.point_count_abbreviated > 10 ? '#0000A3' : '#0A0AFF'
            return new this.localAtlas.HtmlMarker({
              position: position,
              text: properties.point_count_abbreviated,
              color: '#fff',
              htmlContent: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 240 240">
                              <circle cx="120" cy="120" opacity=".6" r="70" fill="${color}" />
                              <circle cx="120" cy="120" opacity=".3" r="90" fill="${color}" />
                              <circle cx="120" cy="120" opacity=".2" r="110" fill="${color}" />
                              <text x="120" y="140" style="font-size:64px;font-family:The Sans,Fallback,sans-serif;" fill="#fff" text-anchor="middle">{text}</text>
                            </svg>`
            })
          } else {
            const popup = new this.localAtlas.Popup({
              pixelOffset: [0, -20],
              content: '<div class="info-window">' +
                          '<div class="info-window-header"><h3>' + distributor.Name + '</h3></div>' +
                          '<div class="info-window-body text"><div class="info-win-top"><p>' + (distributor.Street ?? ' ') + '<br/>' +
                          (distributor.ZIP ?? ' ') + ' ' +
                          (distributor.City ?? ' ') + '<br/>' +
                          (distributor.Country ?? ' ') + '</p></div>' +
                          '<div class="info-win-bottom"><div>' + (distributor.Phone ?? '') +
                          '</div><div><a class="semi-bold no-text-decoration" href="' + that.sanitizeUrl(distributor.WebsiteURL) + '" target="_blank">' + distributor.WebsiteURL + '</a></div>'
                          + that.getTypes(distributor)
            })

            const marker = new this.localAtlas.HtmlMarker({
              position: position ?? [0, 0],
              id: 'marker_' + id,
              htmlContent: `<img src="${location?.origin}/img/maps_star_icon.png" alt="star" />`,
              color: '#048',
              popup: popup})

            if (this.localMap?.events) {
              this.localMap.events.add('click', marker, async () => {
                that.clickedMarker.push(marker)
                that.$emit('marker-opened', distributor)
              })
            }

            if (that.localMap?.events) {
              that.localMap.events.add('close', popup, () => {
                that.$emit('marker-closed', distributor)
              })
            }

            return distributor?.Name ? marker : null
          }

        }
      })

      this.layerIds.push(layer.getId())
      this.localMap?.layers.add(layer)

      // Schedule a check to verify pins were loaded successfully
      this.schedulePinsLoadCheck()
    },
    teardownLayers() {
      if (this.layerIds.length && this.localMap?.layers) {
        try {
          // Only remove layers that actually exist on the map
          const existingLayers = this.localMap.layers.getLayers()
          const layersToRemove = this.layerIds.filter(id =>
            existingLayers.some(layer => layer.getId && layer.getId() === id)
          )
          if (layersToRemove.length > 0) {
            this.localMap.layers.remove(layersToRemove)
          }
        } catch {
          // ignore
        }
      }
      if (this.currentDataSource && this.localMap?.sources) {
        try {
          this.localMap.sources.remove(this.currentDataSource)
        } catch {
          // ignore
        }
      }
      this.layerIds = []
      this.currentDataSource = null
    },
    schedulePinsLoadCheck() {
      // Wait 2 seconds after layer is added to check if pins loaded
      this.pinsLoadCheckTimeoutId = setTimeout(() => {
        this.verifyPinsLoaded()
      }, 2000)
    },
    verifyPinsLoaded() {
      // Check if we have distributors that should have markers
      if (!this.distributors || this.distributors.length === 0) {
        return
      }

      const markers = this.currentDataSource?.getShapes?.() ?? []
      const expected = Array.isArray(this.distributors)
        ? this.distributors.filter(d => d?.gps?.lng && d?.gps?.lat).length
        : 0

      if (!markers || markers.length < expected) {
        this.pinsLoadCheckCounter++

        // If pins didn't load and we haven't exceeded max retries, retry
        if (this.pinsLoadCheckCounter < this.pinsLoadCheckMax) {
          this.teardownLayers()

          // Retry after a delay
          setTimeout(() => {
            this.initMarkerClusterer()
          }, 250)
        } else {
          this.showMapLoadErrorOnce('Pins failed to load after ' + this.pinsLoadCheckMax + ' verification attempts')
          this.pinsLoadCheckCounter = 0
        }
      } else {
        // Pins loaded successfully, reset counter
        this.pinsLoadCheckCounter = 0
        this.toastShown = false
      }
    },
    showMapLoadErrorOnce() {
      if (this.toastShown) {return}
      this.toastShown = true
      this.$toast?.error(this.$tv('mapDataNotAvailable', 'distributorSearch'))
    },
    async focusOnPoints(arrayOfPoints) {
      if (arrayOfPoints && Array.isArray(arrayOfPoints) && arrayOfPoints.length > 0) {
        const arrayOfFeatures = arrayOfPoints.map(point => {
          if (this.localAtlas) {
            return new this.localAtlas.data.Feature(new this.localAtlas.data.Point([point.lng, point.lat]))
          }
        })

        this.localMap?.setCamera({
          bounds: this.localAtlas?.data.BoundingBox.fromData(arrayOfFeatures),
          padding: 100
        })

        if (arrayOfFeatures.length === 1) {
          this.localMap?.setCamera({
            zoom: 13
          })
        }

        if (3 > arrayOfFeatures.length > 1) {
          await this.openMarkerForDistributor(this.focussedDistributor?.Id === null ? this.distributors[0] : this.distributors.find(d => d.Id === this.focussedDistributor?.Id))
        }
      }
    },
    async openMarkerForDistributor(distributor, counter = null) {
      if (this.focussedDistributor?.Id !== null && this.focussedDistributor?.Id !== distributor?.Id) {
        return
      }
      if (counter && counter > 10) {
        if (this.localMap?.layers && this.layerIds.length > 0) {
          try {
            const existingLayers = this.localMap.layers.getLayers()
            const layersToRemove = this.layerIds.filter(id =>
              existingLayers.some(layer => layer.getId && layer.getId() === id)
            )
            if (layersToRemove.length > 0) {
              this.localMap.layers.remove(layersToRemove)
            }
          } catch {
            // ignore
          }
        }
        this.layerIds = []
        await this.initMarkerClusterer()
        await this.openMarkerForDistributor(distributor, 0)
        return
      }

      if (distributor?.gps) {
        await this.focusOnPoints([distributor.gps])
      }

      const markers = this.localMap?.markers?.getMarkers()

      const item = markers?.find(m => m.properties?.id === distributor?.Id)
      if (!item) {
        setTimeout(() => {
          counter = counter !== null ? counter + 1 : 0
          this.openMarkerForDistributor(distributor, counter)
        }, 300)
        return
      }
      this.clearClickedMarkers()
      if (item && !item?.options?.popup?.isOpen()) {
        this.clickedMarker.push(item)
        item.togglePopup()
      }
    },
    getTypes(distributor) {
      let typesPart = ''
      if (distributor?.PartnerTypes && distributor?.PartnerTypes?.length > 0) {
        const infoIcon = '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368" class="icon--svg icon infoIcon"><path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"></path></svg>'
        distributor?.PartnerTypes.forEach(partner => {
          typesPart = typesPart + '<div class="info-win-bottom-type"><div>' + (partner ?? '') + '</div><a href="https://www.fst.com/de/services/logistik-und-supply-chain/haendlernetzwerk/" target="_blank">' + infoIcon + '</a></div>'
        })
      }
      typesPart = typesPart + '</div>'
      return typesPart
    }
  },
  watch: {
    distributorSelected: {
      immediate: false,
      handler(value) {
        if (value && this.distributors.length > 0 && this.distributors[0]?.gps) {
          this.focusOnPoints([this.distributors[0].gps])
        }
      }
    },
    distributors() {
      if (this.loadedAtleastOnce) {
        this.teardownLayers()
        this.initMarkerClusterer()
      }
    },
    points: {
      immediate: false,
      handler(value) {
        if (this.timeoutId) {
          clearTimeout(this.timeoutId)
        }
        if (value.length > 0 && value[0].lng && value[0].lat) {
          this.timeoutId = setTimeout(() => this.focusOnPoints(value), 200)
        } else {
          this.timeoutId = setTimeout(() => this.resetZoom(), 200)
        }
      }
    },
    refreshId: {
      immediate: false,
      async handler(value) {
        if (value >= 0 && !this.mapReadyHandled) {
          this.initMap()
        } else if (value > 0) {
          await this.clearClickedMarkers()
          this.initMarkerClusterer()
        }
      }
    },
    focussedDistributor: {
      immediate: false,
      async handler(newVal) {
        if (newVal) {
          await this.openMarkerForDistributor(newVal)
        }
      }
    }
  }
}
</script>
<style lang="scss">
#AzureAtlas,
#AzureAtlasPlaceHolder {
  min-height: 56rem;
  height: 100%;
  width: 100%;
}

#AzureAtlasPlaceHolder {
  position: absolute;
  top: 0;
  left: 0;
  background-image: url('~/assets/images/placeholder_map.png');
  background-size: 100% 100%;
}

.info-window-header {
  padding: .75rem 2rem .75rem .75rem;
  background-color: $color__primary;
  box-shadow: 0 .25rem .25rem rgb(0 0 0 / 20%);
}

.info-window-body {
  font-weight: lighter;
  display: flex;
  flex-direction: column;
  padding: .75rem;
}

.info-window h3 {
  padding: 0;
  margin: 0;
  font-size: .9rem;
  color: $color__white;
}

.info-window {
  outline: none;
  width: 15rem;

  * {
    white-space: normal;
  }

  a {
    outline: none;
  }
}

[id='AzureAtlas'] .popup-content-container {
  position: relative;

  a {
    word-break: break-all;
  }

  button.popup-close {
    position: absolute;
    color: $color__white;
    font-size: 1.5rem;
    right: .5rem;
    top: .5rem;

    &:hover {
      color: $color__white;
    }
  }
}

.info-win-bottom {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.info-win-bottom-type {
  display: inline-flex;

  .icon {
    margin-left: .125rem;
    margin-top: .1rem;
  }

  .infoIcon  {
    width: 1.0625rem;
    height: 1.0625rem;
    fill:  $color__primary;
  }
}
</style>
